import { useState, useCallback, useEffect } from "react";

import {
  Container,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
} from "@material-ui/core";
import { useWebsocketFetch } from "hooks/useWebsocketFetch";
import verifyProfilesVision from "utils/verifyProfilesVision";
import Api from "lib/api/httpRequestHelper";
import useStructureQueries from "hooks/useStructureQueries";
import { IsMobile } from "utils/is-mobile";
import { useVisionLogin } from "hooks/useVisionLogin";
import moment from "moment";

import FromToDateField from "components/_refactor/fields/from-to-date";
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import StructureFields from "components/_refactor/fields/structure";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import LoadingData from "components/Loading/LoadingData";
import InputText from "components/_refactor/text/input";
import FilterCard from "components/_refactor/filter-card";
import { API_ENV } from "constants/environment";
import TableView from "./components/table-view";

const dataBaseOptions = [
  {
    id: "DATA_EMISSAO",
    Nome: "Data Emissão",
  },
  {
    id: "DATA_VENCIMENTO",
    Nome: "Data Vencimento",
  },
];

const situationOptions = [
  {
    value: "TODOS",
    Nome: "Todos",
    id: 1,
  },
  {
    value: "ABERTOS",
    Nome: "Abertos",
    id: 2,
  },
  {
    value: "PAGOS",
    Nome: "Pagos",
    id: 3,
  },
  {
    value: "VENCIDOS",
    Nome: "Vencidos",
    id: 4,
  },
  {
    value: "AVENCER",
    Nome: "A vencer",
    id: 5,
  },
];

export default function RelatorioAcompanhamentoTitulos() {
  const [values, setValues] = useState({
    dataReferencia: dataBaseOptions[0],
    startDate: "",
    endDate: "",
    situation: situationOptions[0],
    conditionPayment: "",
    nrNF: "",
    nrPedido: "",
    nrProposta: "",
    nrTitulo: "",
    typeService: "",
  });
  const [typeServiceOptions, setTypeServiceOptions] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [structureFilters, setStructureFilters] = useState({});
  const [structureLoading, setStructureLoading] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [searching, setSearching] = useState(false);
  const [isDetailedView, setIsDetailedView] = useState(false);

  const [titles, setTitles] = useState([]);

  const snackbar = useSnackbar();
  const isMobile = IsMobile();
  const { userId } = useVisionLogin();

  const isFranquiado = verifyProfilesVision([
    "FRANQUEADO_ADM",
    "FRANQUEADO_GERENTE",
    "FRANQUEADO_VENDEDOR",
    "FRANQUIAS_ADM",
  ]);

  const handleClearFilters = () => {
    setClearFilters(true);
    setValues({
      dataReferencia: dataBaseOptions[0],
      startDate: "",
      endDate: "",
      situation: situationOptions[0],
      conditionPayment: "",
      nrNF: "",
      nrPedido: "",
      nrProposta: "",
      nrTitulo: "",
      typeService: "",
    });
    setTimeout(() => {
      setClearFilters(false);
    }, 500);
  };

  const fetchConditionsPayment = useCallback(async () => {
    try {
      const response = await Api.get(
        "get-payment-condition-title",
        "financialdocument",
        "",
        true,
      );
      if (response.Success) {
        setConditions(response.Result);
      }
    } catch {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar a lista de condiçoes de pagamento.",
        { severity: "error" },
      );
    }
  }, []);

  const { getListBillingTypes } = useStructureQueries();

  const fetchTypeService = useCallback(async () => {
    try {
      const result = await getListBillingTypes();
      const responseResult = result?.data;
      const items =
        responseResult?.Items ??
        (Array.isArray(responseResult) ? responseResult : []);
      const formattedTypes = items.map((item) => ({
        Nome: item.Nome,
        id: item.IdFranquiaTipoCobranca,
      }));
      setTypeServiceOptions(formattedTypes);
    } catch {
      snackbar.showMessage("Erro ao buscar os tipos de cobrança.", {
        severity: "error",
      });
    }
  }, [getListBillingTypes, snackbar]);

  const { handleFetchUrl, handleFetchData } = useWebsocketFetch({
    onSuccess: (data) => {
      setTitles(data);
      setSearching(false);
    },
    onError: (error) => {
      setSearching(false);
      snackbar.showMessage("Erro ao buscar os títulos: " + error.message, {
        severity: "error",
      });
    },
    onEmpty: () => {
      setSearching(false);
      snackbar.showMessage("Nenhum registro localizado.", {
        severity: "info",
      });
    },
  });

  const handleSearch = async () => {
    setTitles(null);
    setSearching(true);

    const requestBody = {
      action: "get-list-financial-docs",
      params: {
        querystring: {
          idMarca: structureFilters?.idMarca,
          idGrupoEconomico: structureFilters?.idGrupoEconomico,
          idLoja: structureFilters?.idLoja,
          tipoData: values.dataReferencia?.id,
          dtInicial: values?.startDate
            ? moment(values.startDate, "DD-MM-YYYY").format("YYYY-MM-DD")
            : "",
          dtFinal: values?.endDate
            ? moment(values.endDate, "DD-MM-YYYY").format("YYYY-MM-DD")
            : "",
          situacao: values.situation?.value,
          condicaoPagamento: values.conditionPayment?.Descricao || "",
          idTipoCobranca: values.typeService?.id?.toString() || "",
          nrNF: values.nrNF,
          nrPedido:
            values.nrPedido?.replace(/[&/\\#,+()$~%.'":*?<>{}]/g, "-") || "",
          nrProposta:
            values.nrProposta?.replace(/[&/\\#,+()$~%.'":*?<>{}]/g, "-") || "",
          nrTitulo: values.nrTitulo,
          franquia: isFranquiado,
          detail: isDetailedView,
        },
        header: {
          "K-IdUser": userId,
        },
      },
    };
    handleFetchData(requestBody);
  };

  useEffect(() => {
    setSearching(true);

    const fetchData = isFranquiado
      ? fetchTypeService()
      : fetchConditionsPayment();
    Promise.resolve(fetchData).finally(() => setSearching(false));

    handleFetchUrl(
      `wss://1g6jdi30j7.execute-api.us-east-1.amazonaws.com/${API_ENV}`,
    );
  }, []);

  return (
    <>
      <HeaderClientes
        section="Relatório de Acompanhamento de Títulos"
        subsection="Financeiro"
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <FilterCard
          handleSearch={handleSearch}
          disabled={searching}
          searching={searching}
          handleClearFilters={handleClearFilters}
        >
          <StructureFields
            clearFilters={clearFilters}
            onStructureFieldsChange={setStructureFilters}
            disableAll={!!structureLoading?.length}
            onStructureFieldsLoad={setStructureLoading}
            defaultBrand={{
              IdMarca: "TODAS",
              Nome: "Todas",
            }}
            handleSearch={handleSearch}
            hideFields={["channel", "representative"]}
            fieldsOrder={["economicGroup", "store"]}
            franquias={isFranquiado}
          />
          <FromToDateField
            isMobile={isMobile}
            label={"Período"}
            disabled={searching}
            initialDate={values?.startDate}
            finalDate={values?.endDate}
            setInitialDate={(value) =>
              setValues((prev) => ({ ...prev, startDate: value }))
            }
            setFinalDate={(value) =>
              setValues((prev) => ({ ...prev, endDate: value }))
            }
          />
          <Grid item xs={12} md={6} lg={4}>
            <AutoCompleteField
              label="Data de Referência"
              onChange={(value) =>
                setValues((prev) => ({ ...prev, dataReferencia: value }))
              }
              value={values?.dataReferencia}
              options={dataBaseOptions}
              disabled={searching}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <AutoCompleteField
              label="Situação do título"
              onChange={(value) =>
                setValues((prev) => ({ ...prev, situation: value }))
              }
              value={values?.situation}
              options={situationOptions}
              disabled={searching}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4} hidden={isFranquiado}>
            <AutoCompleteField
              label="Condição do Pagamento"
              onChange={(value) =>
                setValues((prev) => ({ ...prev, conditionPayment: value }))
              }
              value={values?.conditionPayment}
              options={conditions}
              disabled={searching}
              optionLabelKey="Descricao"
              optionIdKey="Descricao"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4} hidden={!isFranquiado}>
            <AutoCompleteField
              label="Tipo se serviço"
              value={values?.typeService ?? null}
              onChange={(value) =>
                setValues((prev) => ({ ...prev, typeService: value }))
              }
              disabled={searching}
              options={typeServiceOptions}
              optionLabelKey="label"
              optionIdKey="value"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <InputText
              label="Nº da Nota"
              value={values?.nrNF}
              onChange={(e) =>
                setValues((prev) => ({ ...prev, nrNF: e.target.value }))
              }
              disable={searching}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <InputText
              label="Nº do Pedido"
              value={values?.nrPedido}
              onChange={(e) =>
                setValues((prev) => ({ ...prev, nrPedido: e.target.value }))
              }
              disable={searching}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <InputText
              label="Nº da Proposta"
              value={values?.nrProposta}
              onChange={(e) =>
                setValues((prev) => ({ ...prev, nrProposta: e.target.value }))
              }
              disable={searching}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <InputText
              label="Nº do Título"
              value={values?.nrTitulo}
              onChange={(e) =>
                setValues((prev) => ({ ...prev, nrTitulo: e.target.value }))
              }
              disable={searching}
            />
          </Grid>
        </FilterCard>

        <Grid container spacing={2} style={{ margin: "1rem" }}>
          <Grid item xs={12} itemAlign="flex-end">
            <FormControlLabel
              control={
                <Switch
                  checked={isDetailedView}
                  onChange={(e) => setIsDetailedView(e.target.checked)}
                  disabled={searching}
                />
              }
              label={
                <Typography variant="body2" style={{ marginLeft: 8 }}>
                  {!isDetailedView
                    ? "Visualização Detalhada"
                    : "Visualização Resumida"}
                </Typography>
              }
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12}>
            <LoadingData searching={searching} label="Buscando dados..." />
          </Grid>
        </Grid>

        {!searching && titles?.length > 0 && (
          <TableView
            data={titles}
            viewType={isDetailedView ? "full" : "summarized"}
          />
        )}
      </Container>
    </>
  );
}
